import { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { includes, isEmpty } from 'ramda';
import { useDispatch, useSelector } from 'react-redux';

import { getLabelMessage } from '@gef-ui/localization/messages';

import { ComponentLoader } from 'components';
import PaginationRow from 'components/atoms/GefMui/PaginationRow';
import SortCell from 'components/atoms/GefMui/SortCell';
import TableHasNoResults from 'components/atoms/GefMui/TableHasNoResults';
import { codelists } from 'constants/codelists';
import { EMPTY_VALUES, USER_ROLES } from 'constants/general';
import fetch from 'features/common/fetch';
import campaigns from 'features/views/campaignDashboards/campaigns';
import dashboardsSidebar from 'features/views/campaignDashboards/sidebar';
import { fetchAdditionalUserInfo } from 'features/views/user/actions';
import { getRolesByCurrentRole, getUser } from 'features/views/user/selectors';
import { getCodelistByName } from 'utils/codelists';

import CurrentCampaignsTableRow from './CurrentCampaignsTableRow';

const { urls } = fetch.constants;
const fetchStorePath = `${urls.campaignList}.${campaigns.constants.campaignViews.CURRENT_CAMPAIGNS}`;

let selectCampaignTimeout = null;

const roleToSummaryMap = {
	[USER_ROLES.SELLER]: urls.dashboardSellerSummary,
	[USER_ROLES.GUARANTOR]: urls.dashboardRegionSummary,
	[USER_ROLES.REGION_MANAGER]: urls.dashboardRegionSummary,
	[USER_ROLES.AGENCY_MANAGER]: urls.dashboardRegionSummary,
	[USER_ROLES.DISTRICT_MANAGER]: urls.dashboardRegionSummary,
	[USER_ROLES.GROUP_MANAGER]: urls.dashboardManagerSummary,
	[USER_ROLES.PPZ]: urls.dashboardPpzSummary,
	[USER_ROLES.CENTRAL]: urls.dashboardCentralSummary,
};

export const CurrentCampaignsTable = () => {
	const dispatch = useDispatch();

	const user = useSelector(getUser);
	const userStructures = useSelector(getRolesByCurrentRole);
	const records = useSelector(campaigns.selectors.getCurrentCampaigns);
	const sort = useSelector(campaigns.selectors.getSort);
	const campaignChannels = useSelector(getCodelistByName(codelists.campaignChannels));

	useEffect(() => {
		dispatch(fetchAdditionalUserInfo());
	}, [dispatch]);

	const handleLoadMore = useCallback(() => {
		dispatch(campaigns.actions.currentCampaignActions.loadMore());
	}, [dispatch]);

	const handlePaginationClick = useCallback(
		(e, page) => {
			dispatch(campaigns.actions.currentCampaignActions.loadPage(page - 1));
		},
		[dispatch],
	);

	const handleSort = useCallback(
		(sort) => {
			dispatch(campaigns.actions.sortRecords(sort));
		},
		[dispatch],
	);

	const handleReload = useCallback(() => {
		dispatch(campaigns.actions.currentCampaignActions.reload());
	}, [dispatch]);

	const loadSummary = useCallback(
		(campaign) => {
			const orgStructure = userStructures
				.filter((structure) =>
					structure.role === USER_ROLES.GUARANTOR ? structure.campaignWaveId === campaign.id : true,
				)
				.map((structure) => ({
					channelId: !includes(structure.role, [USER_ROLES.CENTRAL, USER_ROLES.GUARANTOR])
						? structure.channelId
						: undefined,
					regionId: structure.regionId,
					regionalManager:
						roleToSummaryMap[structure.role] === urls.dashboardRegionSummary ? user : undefined,
					agencyIds: structure.agencyId && [structure.agencyId],
					manager: structure.managerLogin,
					seller: structure.sellerLogin,
					ppz: structure.ppzLogin,
				}));

			dispatch(
				dashboardsSidebar.actions.fetchSummary(
					campaign,
					roleToSummaryMap[userStructures[0].role],
					orgStructure,
				),
			);
		},
		[dispatch, user, userStructures],
	);

	const handleCampaignHover = useCallback(
		(campaign) => () => {
			if (!selectCampaignTimeout) {
				selectCampaignTimeout = -1;
				return loadSummary(campaign);
			}

			clearTimeout(selectCampaignTimeout);
			selectCampaignTimeout = setTimeout(() => {
				loadSummary(campaign);
			}, 500);
		},
		[loadSummary],
	);

	const handleMouseLeave = useCallback(() => {
		clearTimeout(selectCampaignTimeout);
		selectCampaignTimeout = null;
		dispatch(dashboardsSidebar.actions.removeSummary());
	}, [dispatch]);

	const hasRecords = !isEmpty(records || EMPTY_VALUES.ARRAY);

	return (
		<ComponentLoader fetchStorePath={fetchStorePath} onReload={handleReload}>
			<TableContainer>
				<Table size="small">
					<TableHead>
						<TableRow>
							<TableCell>
								<SortCell hasRecords={hasRecords} sortBy="campaignName" sort={sort} onSort={handleSort}>
									{getLabelMessage('campaigns:name')}
								</SortCell>
							</TableCell>
							<TableCell>
								<SortCell hasRecords={hasRecords} sortBy="channelId" sort={sort} onSort={handleSort}>
									{getLabelMessage('campaigns:channel')}
								</SortCell>
							</TableCell>
							<TableCell>
								<SortCell hasRecords={hasRecords} sortBy="startDate" sort={sort} onSort={handleSort}>
									{getLabelMessage('campaigns:validityDateFrom')}
								</SortCell>
							</TableCell>
							<TableCell>
								<SortCell hasRecords={hasRecords} sortBy="endDate" sort={sort} onSort={handleSort}>
									{getLabelMessage('campaigns:validityDateTo')}
								</SortCell>
							</TableCell>
							<TableCell width="70" />
						</TableRow>
					</TableHead>
					<TableBody onMouseLeave={handleMouseLeave} className="Campaigns">
						{!hasRecords ? (
							<TableHasNoResults />
						) : (
							records.map((campaign) => (
								<TableRow key={campaign.id} hover onMouseEnter={handleCampaignHover(campaign)}>
									<CurrentCampaignsTableRow campaign={campaign} campaignChannels={campaignChannels} />
								</TableRow>
							))
						)}
					</TableBody>
					<PaginationRow
						fetchStorePath={fetchStorePath}
						colSpan={100}
						onLoadNext={handleLoadMore}
						onPaginationClick={handlePaginationClick}
					/>
				</Table>
			</TableContainer>
		</ComponentLoader>
	);
};

CurrentCampaignsTable.propTypes = {
	campaignChannels: PropTypes.array,
};

export default CurrentCampaignsTable;
